import { Crown, Award, CheckCircle } from 'lucide-react';

const CertificatePreview = () => {
  // Sample data for preview
  const previewData = {
    studentName: "Marcus Johnson",
    courseName: "CDL Mastery Program",
    completionDate: "April 5, 2026",
    certificateId: "QBA-CDL-2026-0405-MJ",
    instructorName: "Queen B Logistics Academy"
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Certificate Preview</h1>
          <p className="text-white/50">This is how the completion certificate will look</p>
        </div>

        {/* Certificate */}
        <div 
          className="relative bg-[#0D0D0D] border-4 border-[#D4AF37] p-12 mx-auto"
          style={{ 
            aspectRatio: '1.414/1',
            maxWidth: '800px',
            background: 'linear-gradient(135deg, #0D0D0D 0%, #1a1a1a 50%, #0D0D0D 100%)'
          }}
        >
          {/* Corner Accents */}
          <div className="absolute top-4 left-4 w-16 h-16 border-t-2 border-l-2 border-[#D4AF37]/50" />
          <div className="absolute top-4 right-4 w-16 h-16 border-t-2 border-r-2 border-[#D4AF37]/50" />
          <div className="absolute bottom-4 left-4 w-16 h-16 border-b-2 border-l-2 border-[#D4AF37]/50" />
          <div className="absolute bottom-4 right-4 w-16 h-16 border-b-2 border-r-2 border-[#D4AF37]/50" />

          {/* Inner Border */}
          <div className="absolute inset-8 border border-[#D4AF37]/20" />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-between text-center py-4">
            
            {/* Logo & Header */}
            <div>
              <div className="flex items-center justify-center gap-3 mb-4">
                <Crown className="w-10 h-10 text-[#D4AF37]" />
                <span className="font-['Barlow_Condensed'] text-3xl font-bold text-[#D4AF37] uppercase tracking-wider">
                  Queen B Academy
                </span>
                <Crown className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <p className="text-white/40 text-sm uppercase tracking-[0.3em]">Certificate of Completion</p>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center py-8">
              <p className="text-white/60 text-lg mb-2">This certifies that</p>
              
              <h2 className="font-['Barlow_Condensed'] text-5xl font-bold text-white mb-4 tracking-wide">
                {previewData.studentName}
              </h2>
              
              <p className="text-white/60 text-lg mb-6">has successfully completed the</p>
              
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-8 py-4 mb-6">
                <h3 className="font-['Barlow_Condensed'] text-3xl font-bold text-[#D4AF37] uppercase">
                  {previewData.courseName}
                </h3>
              </div>

              <div className="flex items-center gap-2 text-white/50">
                <CheckCircle className="w-5 h-5 text-[#D4AF37]" />
                <span>All modules completed with demonstrated proficiency</span>
              </div>
            </div>

            {/* Footer */}
            <div className="w-full">
              <div className="flex items-center justify-between px-8">
                {/* Date */}
                <div className="text-left">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Date Issued</p>
                  <p className="text-white font-medium">{previewData.completionDate}</p>
                </div>

                {/* Seal */}
                <div className="flex flex-col items-center">
                  <div className="w-20 h-20 rounded-full border-2 border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/5">
                    <Award className="w-10 h-10 text-[#D4AF37]" />
                  </div>
                  <p className="text-[#D4AF37] text-xs mt-2 font-medium">VERIFIED</p>
                </div>

                {/* Certificate ID */}
                <div className="text-right">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Certificate ID</p>
                  <p className="text-white font-mono text-sm">{previewData.certificateId}</p>
                </div>
              </div>

              {/* Signature Line */}
              <div className="mt-6 pt-4 border-t border-white/10">
                <p className="text-[#D4AF37] font-['Barlow_Condensed'] text-xl italic">Queen B Logistics</p>
                <p className="text-white/40 text-xs uppercase tracking-wider">Premium Freight Operations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons (Preview Only) */}
        <div className="flex items-center justify-center gap-4 mt-8">
          <button className="px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center gap-2">
            <Award className="w-5 h-5" />
            Download PDF
          </button>
          <button className="px-6 py-3 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors">
            Share on LinkedIn
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-6 bg-[#121212] border border-white/10 max-w-2xl mx-auto">
          <h3 className="text-white font-bold mb-3">Certificate Features:</h3>
          <ul className="space-y-2 text-white/60 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <span>Unique certificate ID for verification</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <span>Downloadable as high-quality PDF</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <span>One-click LinkedIn sharing</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <span>Generated automatically upon course completion</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CertificatePreview;
